import { Button } from '@tempo-io/sui-button';
import { cn } from '@tempo-io/sui-class-utils';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@tempo-io/sui-empty';
import type { JSX, ReactNode } from 'react';
import aiIllustration from '@/assets/EmptyStateAI.png';
import defaultIllustration from '@/assets/EmptyStateDefault.png';
import lighthouseIllustration from '@/assets/EmptyStateLighthouse.png';
import searchIllustration from '@/assets/EmptyStateSearch.png';
import objectivesIllustration from '@/assets/EmptyStateStrategicObjectives.png';

const EMPTY_STATE_ILLUSTRATIONS = {
  default: defaultIllustration,
  ai: aiIllustration,
  search: searchIllustration,
  objectives: objectivesIllustration,
  unavailable: lighthouseIllustration,
} as const;

export type EmptyStateVariant = keyof typeof EMPTY_STATE_ILLUSTRATIONS;

interface EmptyStateAction {
  label: string;
  onClick: () => void;
  icon?: JSX.Element;
}

interface EmptyStateProps {
  title: string;
  description: ReactNode;
  variant?: EmptyStateVariant;
  size?: 'sm' | 'default';
  /** Replaces the illustration, for live status such as a spinner. */
  icon?: JSX.Element;
  primaryAction?: EmptyStateAction;
  primaryActionNode?: ReactNode;
  secondaryAction?: EmptyStateAction;
  footer?: ReactNode;
  className?: string;
  descriptionClassName?: string;
  'data-testid'?: string;
}

export const EmptyState = ({
  title,
  description,
  variant = 'default',
  size = 'default',
  icon,
  primaryAction,
  primaryActionNode,
  secondaryAction,
  footer,
  className,
  descriptionClassName,
  'data-testid': dataTestId,
}: EmptyStateProps) => {
  const resolvedPrimaryAction =
    primaryActionNode !== undefined
      ? primaryActionNode
      : primaryAction && (
          <Button variant="default" onClick={primaryAction.onClick}>
            {primaryAction.icon}
            {primaryAction.label}
          </Button>
        );

  return (
    <Empty
      className={cn('size-full', size === 'default' && 'min-h-80', className)}
      data-testid={dataTestId}
    >
      <EmptyHeader>
        <EmptyMedia variant="default">
          {icon ?? (
            <img
              src={EMPTY_STATE_ILLUSTRATIONS[variant]}
              alt=""
              aria-hidden="true"
              className={cn(size === 'sm' ? 'size-20' : 'size-32', 'object-contain')}
            />
          )}
        </EmptyMedia>
        <EmptyTitle>{title}</EmptyTitle>
      </EmptyHeader>
      <EmptyDescription className={descriptionClassName}>{description}</EmptyDescription>
      {(resolvedPrimaryAction || secondaryAction) && (
        <EmptyContent>
          <div className="flex gap-2">
            {resolvedPrimaryAction}
            {secondaryAction && (
              <Button variant="outline" onClick={secondaryAction.onClick}>
                {secondaryAction.icon}
                {secondaryAction.label}
              </Button>
            )}
          </div>
        </EmptyContent>
      )}
      {footer}
    </Empty>
  );
};
